<script setup lang="ts">
import type { MegamenuNavItem, MegamenuSection } from '~/data/megamenu'
import { megamenuSalesPath } from '~/data/megamenu'
import IconChevronLeft from '~/assets/icons/chevron-left.svg?component'
import IconChevronRight from '~/assets/icons/chevron-right.svg?component'
import IconLightning from '~/assets/icons/lightning-charge-fill.svg?component'

const props = defineProps<{
  item: MegamenuNavItem
  title: string
  /** When set, show only this section's content. When null, show item's primary link + top-level items (e.g. kits). */
  section?: MegamenuSection | null
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'navbarClose'): void
}>()

const localePath = useLocalePath()

function isExternal(href: string) {
  return href.startsWith('http')
}

function resolveHref(href: string) {
  return isExternal(href) ? href : localePath(href)
}

function isSalesSection(section: MegamenuSection) {
  return section.title === 'Sales'
}

/** Single section view: render that section's items/subSections/Sales */
const isSingleSection = computed(() => props.section != null)
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <header class="flex items-center gap-2 shrink-0 border-b border-gray-200 pb-2 px-4">
      <button
        type="button"
        class="p-2 -ml-2 text-stone-700 hover:bg-gray-100 rounded-full"
        aria-label="Back"
        @click="emit('back')"
      >
        <IconChevronLeft class="h-5 w-5" />
      </button>
      <h2 class="text-lg text-center w-full font-semibold text-stone-900">
        {{ title }}
      </h2>
    </header>
    <div class="flex-1 overflow-auto min-h-0">
      <!-- Single section: Sales -->
      <a
        v-if="isSingleSection && section && isSalesSection(section)"
        :href="localePath(megamenuSalesPath)"
        class="flex items-center gap-2 rounded-full bg-amber-100 px-4 py-3 my-2 text-amber-900 no-underline font-medium"
        @click="emit('navbarClose')"
      >
        <IconLightning class="h-5 w-5 shrink-0 text-red-500" />
        {{ section.title }}
      </a>
      <!-- Single section: subSections (e.g. Top Kategorien) -->
      <div
        v-else-if="isSingleSection && section?.subSections?.length"
        class="flex flex-col"
      >
        <template v-for="sub in section.subSections" :key="sub.title">
          <div class="border-b border-gray-100">
            <div class="py-2 pl-4 text-sm font-medium text-stone-500">
              {{ sub.title }}
            </div>
            <ul>
              <li
                v-for="(link, i) in sub.items?.filter((l) => l?.label)"
                :key="`${sub.title}-${i}`"
                class="border-t border-gray-50"
              >
                <a
                  v-if="link.href"
                  :href="resolveHref(link.href)"
                  class="flex items-center justify-between py-2.5 pl-4 text-stone-800 no-underline hover:text-teal-600"
                  :target="isExternal(link.href) ? '_blank' : undefined"
                  :rel="isExternal(link.href) ? 'noopener' : undefined"
                  @click="emit('navbarClose')"
                >
                  {{ link.label }}
                </a>
              </li>
            </ul>
          </div>
        </template>
      </div>
      <!-- Single section: items -->
      <div
        v-else-if="isSingleSection && section?.items?.length"
        class="flex flex-col"
      >
        <template v-if="section.items.length === 1 && section.items[0]?.href">
          <a
            :href="resolveHref(section.items[0].href)"
            class="flex items-center justify-between py-3 text-stone-800 no-underline hover:text-teal-600"
            :target="isExternal(section.items[0].href) ? '_blank' : undefined"
            :rel="isExternal(section.items[0].href) ? 'noopener' : undefined"
            @click="emit('navbarClose')"
          >
            {{ section.items[0].label }}
            <IconChevronRight class="h-4 w-4 text-gray-400" />
          </a>
        </template>
        <ul v-else>
          <li
            v-for="(link, i) in section.items.filter((l) => l?.label)"
            :key="i"
            class="border-t border-gray-50 pl-4"
          >
            <a
              v-if="link.href"
              :href="resolveHref(link.href)"
              class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
              :target="isExternal(link.href) ? '_blank' : undefined"
              :rel="isExternal(link.href) ? 'noopener' : undefined"
              @click="emit('navbarClose')"
            >
              {{ link.label }}
            </a>
          </li>
        </ul>
      </div>
      <!-- No section: item with primaryLink + top-level items (kits) -->
      <template v-else>
        <div v-if="item.primaryLink" class="border-b border-gray-100">
          <a
            :href="resolveHref(item.primaryLink.href)"
            class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
            :target="isExternal(item.primaryLink.href) ? '_blank' : undefined"
            :rel="isExternal(item.primaryLink.href) ? 'noopener' : undefined"
            @click="emit('navbarClose')"
          >
            {{ item.primaryLink.label }}
          </a>
        </div>
        <ul v-if="item.items?.length">
          <li
            v-for="(link, i) in item.items"
            :key="i"
            class="border-t border-gray-50 pl-4"
          >
            <a
              v-if="link.href"
              :href="resolveHref(link.href)"
              class="flex items-center justify-between py-2.5 pl-0 text-stone-800 no-underline hover:text-teal-600"
              :target="isExternal(link.href) ? '_blank' : undefined"
              :rel="isExternal(link.href) ? 'noopener' : undefined"
              @click="emit('navbarClose')"
            >
              {{ link.label }}
            </a>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>
